import { useState, useEffect, useRef } from "react";
import "./carrouselGroups.sass";

/**
 * Componente reutilizable para el funcionamiento del carrousel "MatchesTabs MatchTabBtn",
*/
export default function CarrouselGroups({
  TABS,
  activeGroup,
  onSelect,
  cardWidth = 60,
  classCarrousel = "MatchTabBtnContainer",
  classPrefix = "matches-group",
  groupsState = {}
}) {
  const hasSimulatedTeams = (groupId) => {
    const group = groupsState[groupId];
    return group?.simulationState?.isSimulated ?? false;
  };
  const sliderRef = useRef(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isEndReached, setIsEndReached] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [newWidth, setNewWidth] = useState(0);

  /** Detecta si el contenedor es scrollable al renderizar o redimensionar */
  useEffect(() => {
    const container = sliderRef.current;
    if (container) {
      setIsScrollable(container.scrollWidth > container.clientWidth);
      setIsEndReached(container.scrollLeft + container.clientWidth >= container.scrollWidth - 1);
    }
  }, [TABS, newWidth]);

  /** Detecta redimensionamiento de ventana */
  useEffect(() => {
    const onResize = () => setNewWidth(window.innerWidth);
    setNewWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); };
  }, []);

  // Swipe táctil (izquierda / derecha)
  const handleTouchStart = (e) => setTouchStart(e.targetTouches[0].clientX);
  const handleTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

  const handleSwipe = () => {
    if (((touchStart - touchEnd) > 100) && currentIndex < TABS.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
    if (((touchEnd - touchStart) > 100) && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // Manejo del scroll con botones left / right
  useEffect(() => {
    const container = sliderRef.current;
    if (!container) return;

    const onScroll = () => {
      setScrollLeft(container.scrollLeft);
      setIsEndReached(container.scrollLeft + container.clientWidth >= container.scrollWidth - 1);
    };

    container.addEventListener('scroll', onScroll);
    return () => {
      container.removeEventListener('scroll', onScroll);
      setNewWidth(container?.clientWidth);
    };
  }, []);

  // Scroll automático al tab activo cuando cambia activeGroup
  useEffect(() => {
    const container = sliderRef.current;
    if (!container) return;

    // Buscar el botón activo dentro del contenedor
    const activeTabButton = container.querySelector('.MatchTabBtn.is-active');
    if (!activeTabButton) return;

    // Obtener el contenedor padre del botón (MatchTabBtnContainer)
    const activeTabContainer = activeTabButton.closest(`.${classCarrousel}`);
    if (!activeTabContainer) return;

    // Usar un pequeño delay para asegurar que el DOM está actualizado
    setTimeout(() => {
      const tabOffsetLeft = activeTabContainer.offsetLeft;
      const tabWidth = activeTabContainer.clientWidth;
      const containerWidth = container.clientWidth;

      // Calcular scroll para centrar el tab
      const centerScroll = tabOffsetLeft - (containerWidth / 2) + (tabWidth / 2);

      container.scrollTo({
        left: Math.max(0, centerScroll),
        behavior: 'smooth'
      });
    }, 0);
  }, [activeGroup, classCarrousel]);

  // Función para mover scroll del carrusel a traves de las flechas
  const moveScroll = (direction) => {
    const container = sliderRef.current;
    if (!container) return;

    const items = container.querySelectorAll(`.${classCarrousel}`);
    if (items.length === 0) return;

    const itemWidth = items[0].clientWidth;
    const visibleItems = Math.floor(container.clientWidth / itemWidth);
    const scrollLeftNew = container.scrollLeft;

    const newScroll = direction === 'next'
      ? (Math.ceil(scrollLeftNew / itemWidth) + visibleItems) * itemWidth
      : (Math.floor(scrollLeftNew / itemWidth) - visibleItems) * itemWidth;

    container.scrollTo({ left: newScroll, behavior: 'smooth' });
  };

  return (
    <div className={`${classPrefix}__arrows`}>
      {/* Flecha izquierda si hay scroll */}
      {scrollLeft >= 1 && TABS.length > 0 && (
        <button type="button" className={`${classPrefix}__arrows-arrowL`} onClick={() => moveScroll("prev")} aria-label="Atrás" />
      )}

      {/* Contenedor carrousel deslizable */}
      <div className={`${classPrefix}__slider`} ref={sliderRef}>
        <div
          className={`${classPrefix}__slider-slides`}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleSwipe}
        >
          {TABS.map((g) => (
            <div key={g} className={`${classCarrousel}`}>
              <div
                className={`MatchTabBtn ${g === activeGroup && !hasSimulatedTeams(g) ? "is-active" : ""} ${hasSimulatedTeams(g) ? "is-simulate" : ""}`}
                onClick={() => onSelect(g)}
                role="button"
                tabIndex={0}
              >
                GRUPO {g}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Flecha derecha si hay scroll disponible */}
      {isScrollable && !isEndReached && TABS.length > 0 && (
        <button type="button" className={`${classPrefix}__arrows-arrowR`} onClick={() => moveScroll("next")} aria-label="Adelante" />
      )}
    </div>
  );
}